package dev.wyns.ventrova.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import dev.wyns.ventrova.data.model.Place
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.icon
import dev.wyns.ventrova.ui.theme.palette
import kotlin.math.hypot
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

/** Icon from the bundled Material Symbols. */
@Composable
fun VIcon(
    icon: DrawableResource,
    modifier: Modifier = Modifier,
    size: Dp = 24.dp,
    tint: Color = Color.Unspecified,
    contentDescription: String? = null,
) {
    Icon(
        painter = painterResource(icon),
        contentDescription = contentDescription,
        modifier = modifier.size(size),
        tint = if (tint == Color.Unspecified) LocalContentColor.current else tint,
    )
}

/**
 * The level marker used throughout the design: a filled circle for 100% vegan, half filled for
 * vegetarian and an outlined circle for vegan options.
 */
@Composable
fun LevelGlyph(
    level: VeganLevel,
    modifier: Modifier = Modifier,
    size: Dp = 10.dp,
    borderWidth: Dp = 2.dp,
    /** Width of an extra white ring around the glyph (used on map markers). */
    haloWidth: Dp = 0.dp,
) {
    val palette = level.palette
    Canvas(modifier.size(size + haloWidth * 2)) {
        val halo = haloWidth.toPx()
        val radius = size.toPx() / 2
        val center = Offset(this.size.width / 2, this.size.height / 2)
        if (halo > 0) drawCircle(Color.White, radius + halo, center)
        drawCircle(Color.White, radius, center)
        when (level) {
            VeganLevel.Vegan -> drawCircle(palette.color, radius, center)
            VeganLevel.Vegetarian -> drawArc(
                color = palette.color,
                startAngle = 90f,
                sweepAngle = 180f,
                useCenter = true,
                topLeft = Offset(center.x - radius, center.y - radius),
                size = Size(radius * 2, radius * 2),
            )
            VeganLevel.VeganOptions -> Unit
        }
        val stroke = borderWidth.toPx()
        drawCircle(
            color = palette.color,
            radius = radius - stroke / 2,
            center = center,
            style = Stroke(stroke),
        )
    }
}

/**
 * Placeholder thumbnail (there are no photos yet): diagonal stripes in the level's soft color with
 * the category icon.
 */
@Composable
fun PlaceThumbnail(
    place: Place,
    modifier: Modifier = Modifier,
    shape: Shape,
    iconSize: Dp = 24.dp,
    showIcon: Boolean = true,
    stripeAngle: Int = 135,
) {
    val palette = place.veganLevel.palette
    Box(modifier.clip(shape).stripes(palette.soft, VentrovaColors.StripeWarm, stripeAngle), contentAlignment = Alignment.Center) {
        if (showIcon) VIcon(place.category.icon, size = iconSize, tint = palette.ink)
    }
}

/** Repeating diagonal stripes, like `repeating-linear-gradient(angle, a 0 10px, b 10px 20px)`. */
fun Modifier.stripes(first: Color, second: Color, angle: Int = 135, stripeWidth: Dp = 10.dp): Modifier =
    background(second).drawBehind {
        val width = stripeWidth.toPx()
        val diagonal = hypot(size.width, size.height)
        rotate(degrees = angle - 90f, pivot = center) {
            var x = center.x - diagonal
            while (x < center.x + diagonal) {
                drawRect(first, topLeft = Offset(x, center.y - diagonal), size = Size(width, diagonal * 2))
                x += width * 2
            }
        }
    }

/** Small round surface used for close buttons and similar. */
@Composable
fun CircleButtonBackground(color: Color, size: Dp, modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Box(modifier.size(size).clip(CircleShape).background(color), contentAlignment = Alignment.Center) { content() }
}

/** Thin border helper matching the design's 0.5px hairlines. */
fun Modifier.hairline(shape: Shape, color: Color = VentrovaColors.Hairline) = border(0.5.dp, color, shape)
